import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, Mail } from "lucide-react";
import { useSettings } from "@/lib/content";

const NAV = [
  { label: "Início", to: "/" },
  { label: "Quem Somos", to: "/quem-somos" },
  { label: "Áreas de Atuação", to: "/areas-de-atuacao" },
  { label: "Serviços Jurídicos", to: "/servicos" },
  { label: "Para Quem Atuamos", to: "/para-quem-atuamos" },
  { label: "Palestras e Treinamentos", to: "/palestras-e-treinamentos" },
  { label: "Conteúdo Jurídico", to: "/conteudo-juridico" },
  { label: "Faça Parte", to: "/faca-parte" },
  { label: "Contato", to: "/contato" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { settings } = useSettings();

  const waHref = `https://wa.me/${settings?.contact?.whatsapp || "5519997724509"}`;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${
          scrolled ? "bg-ivory/95 backdrop-blur-md border-navy/10" : "bg-ivory/80 backdrop-blur-sm border-navy/5"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 md:px-8">
          <Link to="/" data-testid="nav-logo-link" aria-label="Silva Cotan — Início" className="shrink-0">
            {settings?.logo_url ? (
              <img src={settings.logo_url} alt="Silva Cotan e Associados — Assessoria Jurídica" className="h-12 md:h-14 w-auto" />
            ) : (
              <span className="font-display text-xl text-navy">Silva Cotan</span>
            )}
          </Link>

          <div className="flex items-center gap-4">
            <nav className="hidden items-center gap-4 xl:flex" aria-label="Menu principal">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-link-${item.to.replace(/\//g, "") || "inicio"}`}
                  className={({ isActive }) =>
                    `link-underline whitespace-nowrap ${isActive ? "active" : ""} text-[10px] font-semibold uppercase tracking-[0.08em] transition-colors ${
                      isActive ? "text-gold-deep" : "text-navy/75 hover:text-navy"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-whatsapp-button"
              className="hidden items-center gap-1.5 whitespace-nowrap rounded-full bg-gold px-4 py-2 text-[10px] font-bold uppercase tracking-[0.1em] text-navy transition-all duration-300 hover:bg-gold-light xl:inline-flex"
            >
              <WhatsAppGlyph /> WhatsApp
            </a>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(true)}
            aria-label="Abrir menu"
            className="flex h-11 w-11 items-center justify-center border border-navy/15 text-navy xl:hidden"
          >
            <Menu size={20} strokeWidth={1.5} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            className="fixed inset-0 z-[60] flex flex-col bg-navy text-ivory grain"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex h-20 items-center justify-between px-5 md:px-8">
              {settings?.logo_url ? (
                <img src={settings.logo_url} alt="Silva Cotan" className="h-12 w-auto rounded-sm" />
              ) : (
                <span className="font-display text-xl">Silva Cotan</span>
              )}
              <button
                data-testid="nav-mobile-close"
                onClick={() => setOpen(false)}
                aria-label="Fechar menu"
                className="flex h-11 w-11 items-center justify-center border border-ivory/20"
              >
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-1 overflow-y-auto px-6" aria-label="Menu mobile">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      `block border-b border-ivory/10 py-3 font-display text-2xl md:text-3xl ${
                        isActive ? "text-gold" : "text-ivory hover:text-gold transition-colors"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="px-6 pb-6">
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-whatsapp-button"
                className="inline-flex items-center justify-center gap-2.5 rounded-full bg-gold px-7 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] text-navy transition-colors hover:bg-gold-light"
              >
                <WhatsAppGlyph /> Falar no WhatsApp
              </a>
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-2 px-6 pb-10 text-xs text-ivory/60">
              {settings?.contact?.phone && (
                <a href={`tel:${settings.contact.phone.replace(/\D/g, "")}`} className="flex items-center gap-2">
                  <Phone size={14} className="text-gold" /> {settings.contact.phone}
                </a>
              )}
              {settings?.contact?.email && (
                <a href={`mailto:${settings.contact.email}`} className="flex items-center gap-2">
                  <Mail size={14} className="text-gold" /> {settings.contact.email}
                </a>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function WhatsAppGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21 5.46 0 9.91-4.45 9.91-9.91C21.95 6.45 17.5 2 12.04 2zm0 18.03c-1.48 0-2.93-.4-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.26 8.26 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24 4.54 0 8.24 3.7 8.24 8.24s-3.7 8.24-8.24 8.24zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.25-.64.81-.78.97-.15.17-.29.19-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.35-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.22.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.14-1.18-.06-.1-.22-.16-.47-.29z" />
    </svg>
  );
}
